import type { VFXEffectConfig, VFXPrimitive } from '../../VFXEffectRegistry';

const MUZZLE_FLASH: VFXEffectConfig = {
  id: 'muzzle_flash',
  name: 'Muzzle Flash',
  category: 'burst',
  description: 'Quick bright flash at projectile spawn point',
  // VFX-evolution M1.S5 demo — the universal cast flash now glows through
  // the bloom layer (glow:true → additive + the glow layer's blur). The
  // visible proof of the M1 substrate; plays on every projectile cast.
  primitives: [
    {
      type: 'circle',
      radius: 20,
      color: 0xFFCC33,
      alpha: 1,
      glow: true,
      tween: {
        alpha: 0,
        scale: 2,
        duration: 350,
        ease: 'Power2'
      }
    },
    // Inner white-hot core for visibility
    {
      type: 'circle',
      radius: 8,
      color: 0xFFFFFF,
      alpha: 0.9,
      glow: true,
      tween: {
        alpha: 0,
        scale: 1.5,
        duration: 200,
        ease: 'Power1'
      }
    }
  ]
};

const SCROLL_BURST: VFXEffectConfig = {
  id: 'scroll_burst',
  name: 'Scroll Burst',
  category: 'burst',
  description: 'Radial particle burst for ninja scroll abilities',
  primitives: [
    // Center burst
    {
      type: 'circle',
      radius: 15,
      color: 0x00FFFF,
      alpha: 0.8,
      depth: 95,
      tween: {
        alpha: 0,
        scale: 2,
        duration: 200,
        ease: 'Power2'
      }
    },
    // Scattered particles
    {
      type: 'circle',
      radius: 3,
      color: 0x00FFFF,
      depth: 94,
      tween: {
        alpha: 0,
        duration: 200,
        ease: 'Power2'
      },
      scatter: { count: 6, radius: 30 }
    }
  ]
};

// =============================================================================
// NINJA / ASSASSIN EFFECTS
// =============================================================================

const NINJA_SCROLL_APPEAR: VFXEffectConfig = {
  id: 'ninja_scroll_appear',
  name: 'Ninja Scroll Appear',
  category: 'burst',
  description: 'Scroll materializes before a throwing star attack',
  primitives: [
    {
      type: 'rect',
      width: 24,
      height: 32,
      color: 0x8B4513,
      depth: 90,
      stroke: { color: 0xD2691E, width: 2 },
      tween: {
        scaleX: 1.3,
        scaleY: 1.1,
        alpha: 0,
        duration: 400,
        ease: 'Power2',
        yoyo: true
      }
    }
  ]
};

const NINJA_SCROLL_SWIRL: VFXEffectConfig = {
  id: 'ninja_scroll_swirl',
  name: 'Ninja Scroll Swirl',
  category: 'burst',
  description: 'Energy swirl around the scroll during charge-up',
  primitives: [
    {
      type: 'ellipse',
      width: 40,
      height: 50,
      color: 0xCCCCCC,
      alpha: 0.6,
      depth: 91,
      tween: {
        rotation: Math.PI * 2,
        scale: 1.5,
        alpha: 0,
        duration: 150,
        ease: 'Power2'
      }
    }
  ]
};

// =============================================================================
// BLINK / TELEPORT EFFECTS
// =============================================================================

const BLINK_TRAIL: VFXEffectConfig = {
  id: 'blink_trail',
  name: 'Blink Trail',
  category: 'burst',
  description: 'Afterimage left at the origin of a blink',
  primitives: [
    {
      type: 'ellipse',
      offsetX: 20,
      offsetY: -10,
      width: 30,
      height: 60,
      color: 0x8844FF,
      alpha: 0.6,
      depth: 50,
      tween: {
        alpha: 0,
        scaleX: 2,
        scaleY: 0.5,
        duration: 400,
        ease: 'Quad.easeOut'
      }
    },
    // Shadow fragments for afterimage visibility
    {
      type: 'circle',
      radius: 5,
      color: 0xAA66FF,
      alpha: 0.7,
      depth: 51,
      tween: {
        alpha: 0,
        scale: 0.3,
        duration: 350,
        ease: 'Power2'
      },
      scatter: { count: 4, radius: 20 }
    }
  ]
};

const BLINK_FLASH: VFXEffectConfig = {
  id: 'blink_flash',
  name: 'Blink Flash',
  category: 'burst',
  description: 'Flash at the destination of a blink',
  primitives: [
    {
      type: 'ellipse',
      offsetX: 20,
      offsetY: -10,
      width: 10,
      height: 30,
      color: 0xAAFFFF,
      alpha: 0.8,
      depth: 50,
      tween: {
        alpha: 0,
        scaleX: 3,
        scaleY: 1,
        duration: 300,
        ease: 'Quad.easeOut'
      }
    },
    // Arrival flash ring for visibility
    {
      type: 'circle',
      radius: 12,
      color: 0x00FFFF,
      alpha: 0.9,
      depth: 51,
      tween: {
        alpha: 0,
        scale: 2,
        duration: 250,
        ease: 'Power1'
      }
    }
  ]
};

// =============================================================================
// BLADE / MELEE EFFECTS
// =============================================================================

const BLADE_SWIRL: VFXEffectConfig = {
  id: 'blade_swirl',
  name: 'Blade Swirl',
  category: 'burst',
  description: 'Spinning blades around the caster (Blade Fury)',
  duration: 650,
  primitives: [
    // Six individually spaced blade silhouettes. A scatter primitive put
    // opposite copies on top of each other at capture time, so six authored
    // blades read as only three stacked discs.
    ...Array.from({ length: 6 }, (_, i): VFXPrimitive => ({
      type: 'polygon',
      polygonPoints: [-25, -4, 13, -4, 29, 0, 13, 4, -25, 4, -32, 0],
      color: i % 2 === 0 ? 0xF7FBFF : 0xAFC8E8,
      alpha: 0.98,
      depth: 101 + i,
      stroke: { color: 0xFFFFFF, width: 1 },
      orbit: {
        radius: 62,
        speed: 1.8,
        startAngle: (i / 6) * Math.PI * 2,
        orientToPath: true,
      },
    })),
    // Bright outer ring makes the caster-centered AOE footprint explicit.
    {
      type: 'shockwave',
      shockwaveInnerRadius: 52,
      shockwaveOuterRadius: 74,
      color: 0xCCD8E6,
      alpha: 0.72,
      depth: 98,
      stroke: { color: 0xFFFFFF, width: 3 },
      tween: {
        scale: 1.12,
        alpha: 0,
        duration: 500,
        ease: 'Power2'
      }
    },
    // Center swirl
    {
      type: 'ellipse',
      width: 70,
      height: 32,
      color: 0x99AACC,
      alpha: 0.45,
      depth: 97,
      glow: true,
      tween: {
        scale: 1.5,
        alpha: 0,
        duration: 300,
        ease: 'Power2'
      }
    }
  ]
};

// =============================================================================
// SHADOW / STEALTH EFFECTS
// =============================================================================

const SHADOW_PUFF: VFXEffectConfig = {
  id: 'shadow_puff',
  name: 'Shadow Puff',
  category: 'burst',
  description: 'Dark smoke burst for shadow abilities',
  primitives: [
    // Scattered smoke particles
    {
      type: 'circle',
      radius: 8,
      color: 0x222222,
      alpha: 0.7,
      depth: 90,
      tween: {
        alpha: 0,
        scale: 2,
        duration: 400,
        ease: 'Power2'
      },
      scatter: { count: 8, radius: 30 }
    },
    // Center burst
    {
      type: 'ellipse',
      width: 40,
      height: 20,
      color: 0x111111,
      alpha: 0.8,
      depth: 89,
      tween: {
        scaleX: 2,
        scaleY: 2,
        alpha: 0,
        duration: 500,
        ease: 'Power2'
      }
    }
  ]
};

// =============================================================================
// POISON EFFECTS
// =============================================================================

const POISON_SPLASH: VFXEffectConfig = {
  id: 'poison_splash',
  name: 'Poison Splash',
  category: 'burst',
  description: 'Green toxic splash (Venomous Stab)',
  primitives: [
    // Main splash
    {
      type: 'ellipse',
      width: 40,
      height: 30,
      color: 0x00FF00,
      alpha: 0.7,
      depth: 95,
      tween: {
        scaleX: 2,
        scaleY: 1.5,
        alpha: 0,
        duration: 400,
        ease: 'Power2'
      }
    },
    // Poison drops
    {
      type: 'circle',
      radius: 4,
      color: 0x00FF00,
      alpha: 0.9,
      depth: 96,
      tween: {
        alpha: 0,
        duration: 500,
        ease: 'Power2'
      },
      scatter: { count: 6, radius: 25 }
    }
  ]
};

export const DEFAULT_CAST_VFX: VFXEffectConfig[] = [
  MUZZLE_FLASH,
  SCROLL_BURST,
  NINJA_SCROLL_APPEAR,
  NINJA_SCROLL_SWIRL,
  BLINK_TRAIL,
  BLINK_FLASH,
  BLADE_SWIRL,
  SHADOW_PUFF,
  POISON_SPLASH,
];
